import isEmail from 'validator/lib/isEmail';
import isFloat from 'validator/lib/isFloat';
import isInt from 'validator/lib/isInt';
import isDate from 'validator/lib/isDate';
import { Dictionary, ValidatorResult, TipLevels } from './types';

interface IsIntOptions {
  min?: number;
  max?: number;
  allow_leading_zeroes?: boolean;
  lt?: number;
  gt?: number;
}

const ONE_DIGIT_PATTERN = /[0-9]/;
const ONE_LOWERCASE_LETTER_PATTERN = /[a-z]/;
const ONE_UPPERCASE_LETTER_PATTERN = /[A-Z]/;
const LATIN_LETTERS_ONLY_PATTERN = /^[A-Za-z\s]+$/;
const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_MAX_LENGTH = 100;

export const VALIDATION_TYPE_REQUIRED = 'validators:required';
export const VALIDATION_TYPE_VALID_PASSWORD = 'validators:validPassword';
export const VALIDATION_TYPE_EMAIL = 'validators:email';
export const VALIDATION_TYPE_MIN = 'validators:min';
export const VALIDATION_TYPE_MAX = 'validators:max';
export const VALIDATION_TYPE_INT = 'validators:int';
export const VALIDATION_TYPE_FLOAT = 'validators:float';
export const VALIDATION_TYPE_LATIN_LETTERS = 'validators:latinOnly';
export const VALIDATION_REGEXP = 'validators:regexp';
export const VALIDATION_DATE = 'validators:date';

export const required =
  (message: string = VALIDATION_TYPE_REQUIRED, options?: Dictionary) =>
  (value: any): ValidatorResult => {
    const stringIsEmpty = typeof value === 'string' && value.replace(/\s/g, '').length === 0;

    return (!value || stringIsEmpty) && { key: message, options };
  };

export const validPassword =
  (message: string = VALIDATION_TYPE_VALID_PASSWORD, options?: Dictionary) =>
  (value: string): ValidatorResult =>
    value &&
    (!ONE_DIGIT_PATTERN.test(value) ||
      !ONE_LOWERCASE_LETTER_PATTERN.test(value) ||
      !ONE_UPPERCASE_LETTER_PATTERN.test(value) ||
      value.length < PASSWORD_MIN_LENGTH ||
      value.length > PASSWORD_MAX_LENGTH)
      ? { key: message, options }
      : false;

export const email =
  (message: string = VALIDATION_TYPE_EMAIL, options?: Dictionary) =>
  (value: string) =>
    value && !isEmail(value) ? { key: message, options } : false;

export const int =
  (settings?: IsIntOptions, message = VALIDATION_TYPE_INT, options?: Dictionary) =>
  (value: string) =>
    value && !isInt(value, settings) ? { key: message, options } : false;

export const min =
  (min: number, message: string = VALIDATION_TYPE_MIN, options?: Dictionary) =>
  (value: number) =>
    value && value < min ? { key: message, options: { min, ...options } } : false;

export const max =
  (max: number, message: string = VALIDATION_TYPE_MAX, options?: Dictionary) =>
  (value: number) =>
    value && (value > max || value > Number.MAX_SAFE_INTEGER)
      ? {
          key: message,
          options: { max, ...options }
        }
      : false;

export const float =
  (message: string = VALIDATION_TYPE_FLOAT, options?: Dictionary) =>
  (value: string) =>
    value && !isFloat(value) ? { key: message, options } : false;

export const latinLettersOnly =
  (message: string = VALIDATION_TYPE_LATIN_LETTERS, options?: Dictionary) =>
  (value: string): ValidatorResult =>
    value && !LATIN_LETTERS_ONLY_PATTERN.test(value) ? { key: message, options } : false;

export const regexp =
  (expr: RegExp, message = VALIDATION_REGEXP, options?: Dictionary) =>
  (value: string): ValidatorResult =>
    value && !expr.test(value) ? { key: message, options } : false;

export const date =
  (format: string, message?: string) =>
  (value: string): ValidatorResult =>
    value && !isDate(value, { format, delimiters: ['.', '/', '-'] })
      ? {
          key: message || 'validator:date',
          options: {}
        }
      : false;

export const tip =
  (message: string, options?: Dictionary, level = TipLevels.TIP) =>
  () => ({ key: message, options, level });
